import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import {
  Calendar,
  User,
  Timer,
  CheckCircle,
  Hourglass,
  ArrowLeft,
  Moon,
  Lock,
  Send,
  Copy,
  X
} from 'lucide-react';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface SlotDia {
  fecha: string;        // ISO 'YYYY-MM-DD'
  diaNombre: string;    // 'Lunes', 'Martes', ...
  diaNumero: string;    // '16', '17', ...
  mesCorto: string;     // 'Jun', 'Jul', ...
}

interface SlotHora {
  inicio: string;       // '09:00'
  fin: string;          // '09:30'
  label: string;        // '9:00 AM'
  disponible: boolean;
}

type Paso = 'slot' | 'metodo_pago' | 'comprobante' | 'espera' | 'confirmada';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DIAS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function formatearHora(hora24: string): string {
  const [h, m] = hora24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function generarProximosDias(cantidad = 14): SlotDia[] {
  const dias: SlotDia[] = [];
  const hoy = new Date();
  for (let i = 0; i < cantidad; i++) {
    const d = new Date(hoy);
    d.setDate(hoy.getDate() + i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    dias.push({
      fecha: `${yyyy}-${mm}-${dd}`,
      diaNombre: DIAS_ES[d.getDay()],
      diaNumero: dd,
      mesCorto: MESES_CORTOS[d.getMonth()],
    });
  }
  return dias;
}

function generarSlotsDeHorario(inicio: string, fin: string, duracionMin = 30, descansoMin = 0): SlotHora[] {
  const slots: SlotHora[] = [];
  const [hIni, mIni] = inicio.split(':').map(Number);
  const [hFin, mFin] = fin.split(':').map(Number);
  let totalMin = hIni * 60 + mIni;
  const finMin = hFin * 60 + mFin;
  while (totalMin + duracionMin <= finMin) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const slotIni = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const slotFinMin = totalMin + duracionMin;
    const hF = Math.floor(slotFinMin / 60);
    const mF = slotFinMin % 60;
    const slotFin = `${String(hF).padStart(2, '0')}:${String(mF).padStart(2, '0')}`;
    slots.push({
      inicio: slotIni,
      fin: slotFin,
      label: formatearHora(slotIni),
      disponible: true,
    });
    totalMin += (duracionMin + descansoMin);
  }
  return slots;
}

function useBookingAvailability(profesionalUuid: string, diaSeleccionado: SlotDia | null) {
  const [cargandoDisponibilidad, setCargandoDisponibilidad] = useState(true);
  const [disponibilidad, setDisponibilidad] = useState<Record<string, any>>({});
  const [duracionMin, setDuracionMin] = useState(30);
  const [descansoMin, setDescansoMin] = useState(0);
  const [anticipacionHoras, setAnticipacionHoras] = useState(24);
  const [politicasAgendamiento, setPoliticasAgendamiento] = useState<string | null>(null);
  const [aceptaPagoPostReserva, setAceptaPagoPostReserva] = useState(false);
  const [aceptaPagoAnticipado, setAceptaPagoAnticipado] = useState(true);
  
  // Guardaremos los slots ocupados obtenidos del servidor
  const [rangosOcupados, setRangosOcupados] = useState<{start: number, end: number}[]>([]);

  useEffect(() => {
    if (!profesionalUuid) return;
    const cargarConfig = async () => {
      try {
        const { data } = await supabase
          .from('profesional_config_agenda')
          .select('disponibilidad_jsonb, politicas_agendamiento, acepta_pago_post_reserva, acepta_pago_anticipado')
          .eq('profesional_uuid', profesionalUuid)
          .single();
        const jsonb = data?.disponibilidad_jsonb || {};
        if (jsonb.dias && typeof jsonb.dias === 'object') {
          setDisponibilidad(jsonb.dias);
          setDuracionMin(jsonb.duracion_minutos || 30);
          setDescansoMin(jsonb.brecha_minutos || 0);
          setAnticipacionHoras(jsonb.anticipacion_horas ?? 24);
        } else {
          setDisponibilidad(jsonb);
        }
        setPoliticasAgendamiento(data?.politicas_agendamiento || null);
        setAceptaPagoPostReserva(data?.acepta_pago_post_reserva ?? false);
        setAceptaPagoAnticipado(data?.acepta_pago_anticipado ?? true);
      } catch (e) {
        console.error('Error cargando config de agenda:', e);
        setDisponibilidad({});
      }
    };
    cargarConfig();
  }, [profesionalUuid]);

  // Al seleccionar un día, consultamos al Motor de Disponibilidad (RPC)
  useEffect(() => {
    if (!profesionalUuid || !diaSeleccionado) {
      setCargandoDisponibilidad(false);
      setRangosOcupados([]);
      return;
    }

    const fetchOcupados = async () => {
      setCargandoDisponibilidad(true);
      try {
        const p_fecha_inicio = `${diaSeleccionado.fecha}T00:00:00-04:00`;
        const p_fecha_fin = `${diaSeleccionado.fecha}T23:59:59-04:00`;
        
        const { data, error } = await supabase.rpc('fn_obtener_slots_ocupados', {
          p_profesional_uuid: profesionalUuid,
          p_fecha_inicio,
          p_fecha_fin
        });

        if (error) throw error;

        if (data && data.length > 0) {
          const ocupados = data.map((r: any) => ({
            start: new Date(r.inicio).getTime(),
            end: new Date(r.fin).getTime()
          }));
          setRangosOcupados(ocupados);
        } else {
          setRangosOcupados([]);
        }
      } catch (err) {
        console.error('Error obteniendo slots ocupados:', err);
        setRangosOcupados([]);
      } finally {
        setCargandoDisponibilidad(false);
      }
    };
    
    fetchOcupados();
  }, [profesionalUuid, diaSeleccionado]);

  const slotsDia: SlotHora[] = React.useMemo(() => {
    if (!diaSeleccionado) return [];
    const horario = disponibilidad[diaSeleccionado.diaNombre];
    if (!horario?.activo) return [];

    const mapSlot = (slot: SlotHora, year: number, month: number, day: number, limiteAnticipacion: Date) => {
      const [h, m] = slot.inicio.split(':').map(Number);
      const slotDate = new Date(year, month - 1, day, h, m, 0);
      const slotDateEnd = new Date(slotDate.getTime() + duracionMin * 60000);
      
      // Anti-pasado / Anticipación
      if (slotDate <= limiteAnticipacion) {
        slot.disponible = false;
      }

      // Colisiones con BD (Allen's Interval)
      const slotStartTs = slotDate.getTime();
      const slotEndTs = slotDateEnd.getTime();
      
      const hasOverlap = rangosOcupados.some(r => 
        (slotStartTs < r.end) && (slotEndTs > r.start)
      );

      if (hasOverlap) {
        slot.disponible = false;
      }

      return slot;
    };

    const processSlots = (slotsList: SlotHora[]) => {
      const ahora = new Date();
      // Agregar la ventana de anticipación
      const limiteAnticipacion = new Date(ahora.getTime() + anticipacionHoras * 60 * 60 * 1000);
      const [year, month, day] = diaSeleccionado.fecha.split('-').map(Number);
      
      return slotsList.map(slot => mapSlot(slot, year, month, day, limiteAnticipacion));
    };

    if (Array.isArray(horario.intervalos) && horario.intervalos.length > 0) {
      let allSlots: SlotHora[] = [];
      for (const iv of horario.intervalos) {
        allSlots = allSlots.concat(generarSlotsDeHorario(iv.inicio, iv.fin, duracionMin, descansoMin));
      }
      return processSlots(allSlots);
    }
    if (horario.inicio && horario.fin) {
      return processSlots(generarSlotsDeHorario(horario.inicio, horario.fin, duracionMin, descansoMin));
    }
    return [];
  }, [diaSeleccionado, disponibilidad, duracionMin, descansoMin, anticipacionHoras, rangosOcupados]);

  const diaActivo = (dia: SlotDia) => {
    const horario = disponibilidad[dia.diaNombre];
    return horario?.activo === true;
  };

  return { cargandoDisponibilidad, slotsDia, diaActivo, politicasAgendamiento, aceptaPagoPostReserva, aceptaPagoAnticipado };
}

function useBookingFlow({
  profesionalUuid, diaSeleccionado, horaSeleccionada, banco, referencia, telefono, monto, citaId, setCitaId, setExpiresAt, setSegundosRestantes, setPaso, navigate, handleBack, showAlert, showConfirm, servicioSeleccionado
}: any) {
  const [enviandoReserva, setEnviandoReserva] = useState(false);

  const handleReservar = async (pagoPostReserva: boolean = false) => {
    if (!diaSeleccionado || !horaSeleccionada) return;
    setEnviandoReserva(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showConfirm(
          'Inicia sesión para reservar',
          'Necesitas una cuenta para agendar citas. ¿Deseas iniciar sesión?',
          () => navigate('/auth/login')
        );
        setEnviandoReserva(false);
        return;
      }

      let mutationId: string;
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        mutationId = crypto.randomUUID();
      } else {
        if (import.meta.env.PROD) {
          throw new Error('Entorno de ejecución no seguro. Se requiere una conexión segura (HTTPS) para realizar reservas.');
        }
        console.warn('Advertencia de seguridad: Usando generador UUID inseguro en entorno de desarrollo local.');
        mutationId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.trunc(Math.random() * 16); // NOSONAR
          const v = c === 'x' ? r : ((r & 3) + 8);
          return v.toString(16);
        });
      }

      const fechaInicio = `${diaSeleccionado.fecha}T${horaSeleccionada.inicio}:00-04:00`;
      const fechaFin   = `${diaSeleccionado.fecha}T${horaSeleccionada.fin}:00-04:00`;
      const { data, error } = await supabase.rpc('fn_reservar_slot_cita', {
        p_profesional_uuid:  profesionalUuid,
        p_fecha_hora_inicio: fechaInicio,
        p_fecha_hora_fin:    fechaFin,
        p_mutation_id:       mutationId,
        p_servicio_id:       servicioSeleccionado?.id || null,
        p_pago_post_reserva: pagoPostReserva,
      });
      if (error || !data?.success) {
        showAlert(
          'No pudimos reservar',
          data?.message || error?.message || 'No pudimos confirmar tu reserva. Por favor intenta de nuevo.'
        );
        return;
      }
      
      if (pagoPostReserva) {
        // La cita queda confirmada directamente
        setPaso('confirmada');
      } else {
        // Flujo normal con TTL y comprobante
        setCitaId(data.cita_id);
        setExpiresAt(new Date(data.expires_at));
        setSegundosRestantes(data.ttl_minutos * 60);
        setPaso('comprobante');
      }
    } catch (err: any) {
      console.error(err);
      showAlert(
        'Error inesperado',
        `Ocurrió un error inesperado: ${err?.message || err}. Por favor intenta de nuevo.`
      );
    } finally {
      setEnviandoReserva(false);
    }
  };

  const handleEnviarComprobante = async () => {
    if (!banco.trim() || referencia.length !== 6 || !telefono.trim() || !monto) {
      showAlert('Datos incompletos', 'Completa todos los campos del comprobante para continuar.');
      return;
    }
    if (!citaId) return;
    setEnviandoReserva(true);
    try {
      const { error } = await supabase
          .from('cita_comprobantes_pago')
          .insert({
            cita_id:              citaId,
            banco_emisor:         banco.trim(),
            referencia_6_digitos: referencia.trim(),
            telefono_emisor:      telefono.trim(),
            monto_pagado:         Number.parseFloat(monto.replaceAll('.', '').replace(',', '.')),
          });
      if (error) {
        if (error.code === '42501') {
          showAlert(
            'Tiempo vencido',
            'El tiempo de reserva venció antes de poder registrar tu pago. Por favor, inicia una nueva reserva.',
            () => handleBack()
          );
          return;
        }
        showAlert('Error de registro', 'No pudimos registrar tu comprobante. Intenta de nuevo.');
        return;
      }
      setPaso('espera');
    } catch {
      showAlert('Error de conexión', 'Ocurrió un error al registrar el comprobante. Por favor intenta de nuevo.');
    } finally {
      setEnviandoReserva(false);
    }
  };

  return { handleReservar, handleEnviarComprobante, enviandoReserva };
}

// ─── Componentes Hijos ────────────────────────────────────────────────────────

const PasoComprobante = ({
  diaSeleccionado, horaSeleccionada, profesionalNombre, profesionalTitulo,
  segundosRestantes, formatTTL, banco, setBanco, referencia, setReferencia,
  telefono, setTelefono, monto, setMonto, servicioSeleccionado, pagoMovilInfo,
  politicasAgendamiento, politicasAceptadas, setPoliticasAceptadas, setMostrarPoliticas
}: any) => (
  <div style={styles.scrollContent}>
    {/* Resumen de la reserva */}
    <div style={styles.reservaSummaryCard}>
      {servicioSeleccionado && (
        <div style={styles.reservaSummaryRow}>
          <div style={{...styles.reservaSummaryRow, flex: 1}}>
            <Calendar size={18} color="#0052CC" />
            <span style={styles.reservaSummaryText}>
              {servicioSeleccionado.titulo_opcion}
            </span>
          </div>
          <div style={{ backgroundColor: '#DBEAFE', padding: '4px 8px', borderRadius: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 800, color: '#1E3A8A' }}>
              {servicioSeleccionado.moneda === 'EUR' ? '€' : '$'}{Number(servicioSeleccionado.precio_sugerido).toFixed(2)}
            </span>
          </div>
        </div>
      )}
      <div style={styles.reservaSummaryRow}>
        <Calendar size={18} color="#475569" />
        <span style={{...styles.reservaSummaryText, color: '#475569', fontWeight: 500}}>
          {diaSeleccionado?.diaNumero} de {diaSeleccionado?.mesCorto} · {horaSeleccionada?.label}
        </span>
      </div>
      <div style={styles.reservaSummaryRow}>
        <User size={18} color="#475569" />
        <span style={{...styles.reservaSummaryText, color: '#475569', fontWeight: 500}}>
          {profesionalNombre} — {profesionalTitulo}
        </span>
      </div>
    </div>

    {/* TTL Countdown */}
    <div style={{ ...styles.ttlBanner, ...(segundosRestantes < 300 ? styles.ttlBannerUrgente : {}) }}>
      <Timer size={20} color={segundosRestantes < 300 ? '#DC2626' : '#0052CC'} />
      <span style={{ ...styles.ttlText, ...(segundosRestantes < 300 ? styles.ttlTextUrgente : {}) }}>
        Tienes <span style={styles.ttlClock}>{formatTTL(segundosRestantes)}</span> para completar el pago
      </span>
    </div>

    {/* Datos del Médico para el Pago Móvil */}
    {pagoMovilInfo && (
      <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#334155', marginBottom: '12px', marginTop: 0 }}>Datos para realizar el Pago Móvil</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <span style={{ fontSize: '12px', color: '#64748B', display: 'block', marginBottom: '2px' }}>Banco</span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>{pagoMovilInfo.banco}</span>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: '#64748B', display: 'block', marginBottom: '2px' }}>Teléfono</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>{pagoMovilInfo.telefono}</span>
              <button onClick={() => globalThis.navigator.clipboard.writeText(pagoMovilInfo.telefono.replace(/\D/g, ''))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex' }} title="Copiar teléfono">
                <Copy size={14} color="#0052CC" />
              </button>
            </div>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: '#64748B', display: 'block', marginBottom: '2px' }}>Cédula / RIF</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>{pagoMovilInfo.cedula}</span>
              <button onClick={() => globalThis.navigator.clipboard.writeText(pagoMovilInfo.cedula.replace(/\D/g, ''))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex' }} title="Copiar Cédula / RIF">
                <Copy size={14} color="#0052CC" />
              </button>
            </div>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: '#64748B', display: 'block', marginBottom: '2px' }}>Monto a pagar</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 800, color: '#1E3A8A' }}>Bs. {monto}</span>
              <button onClick={() => globalThis.navigator.clipboard.writeText(String(monto))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex' }} title="Copiar Monto">
                <Copy size={14} color="#0052CC" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    <h2 style={styles.sectionLabel}>Tus datos de pago</h2>
    <p style={styles.sectionCaption}>
      Una vez realizada la transferencia, completa los datos de tu comprobante.
    </p>

    {/* Campos del comprobante */}
    <div style={styles.inputGroup}>
      <label htmlFor="banco" style={styles.inputLabel}>Banco emisor</label>
      <input
        id="banco"
        style={styles.input}
        placeholder="Ej: Banesco, Mercantil, Venezuela..."
        value={banco}
        onChange={(e) => setBanco(e.target.value.replace(/[^a-zA-Z0-9 áéíóúÁÉÍÓÚñÑ]/g, '').slice(0, 50))}
      />
    </div>

    <div style={styles.inputGroup}>
      <label htmlFor="referencia" style={styles.inputLabel}>Referencia (últimos 6 dígitos)</label>
      <input
        id="referencia"
        style={styles.input}
        placeholder="123456"
        value={referencia}
        onChange={(e) => setReferencia(e.target.value.replace(/\D/g, '').slice(0, 6))}
        maxLength={6}
      />
      {referencia.length > 0 && referencia.length < 6 && (
        <span style={styles.inputHint}>Deben ser exactamente 6 dígitos</span>
      )}
    </div>

    <div style={styles.inputGroup}>
      <label htmlFor="telefono" style={styles.inputLabel}>Teléfono del Pago Móvil</label>
      <input
        id="telefono"
        style={styles.input}
        placeholder="04141234567"
        value={telefono}
        onChange={(e) => setTelefono(e.target.value.replace(/\D/g, '').slice(0, 15))}
      />
    </div>


    <div style={{ height: '120px' }} />
  </div>
);

const PasoMetodoPago = ({
  aceptaPagoAnticipado,
  aceptaPagoPostReserva,
  politicasAgendamiento,
  politicasAceptadas,
  setPoliticasAceptadas,
  setMostrarPoliticas,
  modoPagoSeleccionado,
  setModoPagoSeleccionado
}: any) => {
  const soloAnticipado = aceptaPagoAnticipado && !aceptaPagoPostReserva;
  const soloPostReserva = !aceptaPagoAnticipado && aceptaPagoPostReserva;
  
  // Set default if only one option is available
  useEffect(() => {
    if (soloAnticipado) setModoPagoSeleccionado('anticipado');
    else if (soloPostReserva) setModoPagoSeleccionado('post_reserva');
  }, [soloAnticipado, soloPostReserva, setModoPagoSeleccionado]);

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
      <h2 style={styles.sectionLabel}>¿Cómo deseas pagar?</h2>
      <p style={styles.sectionCaption}>
        El profesional permite las siguientes modalidades de pago para la reserva.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
        {aceptaPagoAnticipado && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '16px', borderRadius: '12px', border: modoPagoSeleccionado === 'anticipado' ? `2px solid #0052CC` : '1px solid #E2E8F0', backgroundColor: modoPagoSeleccionado === 'anticipado' ? `#0052CC08` : '#FFF', transition: 'all 0.2s' }}>
            <input 
              type="radio" 
              name="modo_pago_paciente" 
              checked={modoPagoSeleccionado === 'anticipado'} 
              onChange={() => setModoPagoSeleccionado('anticipado')} 
              style={{ marginTop: '4px', accentColor: '#0052CC', width: '20px', height: '20px' }}
              aria-label="Pago Anticipado"
            />
            <div>
              <span style={{ display: 'block', fontWeight: 600, color: '#0F172A', fontSize: '15px' }}>Pago Anticipado (Pago Móvil)</span>
              <span style={{ display: 'block', fontSize: '13px', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Paga ahora y asegura tu cita de inmediato. Tendrás 30 minutos para subir tu comprobante.</span>
            </div>
          </label>
        )}

        {aceptaPagoPostReserva && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '16px', borderRadius: '12px', border: modoPagoSeleccionado === 'post_reserva' ? `2px solid #0052CC` : '1px solid #E2E8F0', backgroundColor: modoPagoSeleccionado === 'post_reserva' ? `#0052CC08` : '#FFF', transition: 'all 0.2s' }}>
            <input 
              type="radio" 
              name="modo_pago_paciente" 
              checked={modoPagoSeleccionado === 'post_reserva'} 
              onChange={() => setModoPagoSeleccionado('post_reserva')} 
              style={{ marginTop: '4px', accentColor: '#0052CC', width: '20px', height: '20px' }}
              aria-label="Pago Presencial"
            />
            <div>
              <span style={{ display: 'block', fontWeight: 600, color: '#0F172A', fontSize: '15px' }}>Pago en Consulta (Presencial)</span>
              <span style={{ display: 'block', fontSize: '13px', color: '#64748B', marginTop: '4px', lineHeight: 1.4 }}>Reserva gratis ahora y paga el día de la cita directamente en la clínica.</span>
            </div>
          </label>
        )}
      </div>

      {politicasAgendamiento && (
        <div style={{ marginTop: '32px', backgroundColor: '#F8FAFC', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <input 
            type="checkbox" 
            id="politicas" 
            checked={politicasAceptadas}
            onChange={(e) => setPoliticasAceptadas(e.target.checked)}
            style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
          />
          <label htmlFor="politicas" style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, cursor: 'pointer', flex: 1 }}>
            He leído y acepto las <button type="button" onClick={(e) => { e.preventDefault(); setMostrarPoliticas(true); }} style={{ background: 'none', border: 'none', padding: 0, color: '#0052CC', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer', font: 'inherit' }}>Políticas de Agendamiento</button> de este profesional. Reconozco que al proceder con la reserva estoy de acuerdo con estos términos.
          </label>
        </div>
      )}
    </div>
  );
};

const MetodoPagoFooter = ({ puedeContinuar, handleReservar, enviandoReserva }: any) => (
  <div style={styles.footerContainer}>
    <button 
      style={{ ...styles.btnContinuar, ...(puedeContinuar && !enviandoReserva ? {} : styles.btnContinuarOff) }}
      disabled={!puedeContinuar || enviandoReserva}
      onClick={handleReservar}
    >
      {enviandoReserva ? 'Procesando...' : 'Continuar con la Reserva'}
    </button>
  </div>
);

const PasoConfirmada = ({ profesionalNombre, diaSeleccionado, horaSeleccionada, handleBack }: any) => (
  <div style={styles.esperaContainer}>
    <div style={styles.esperaIcono}>
      <CheckCircle size={64} color="#10B981" />
    </div>
    <h2 style={styles.esperaTitulo}>¡Cita Confirmada!</h2>
    <p style={styles.esperaSubtitulo}>
      Tu reserva con el Dr. {profesionalNombre} ha sido agendada con éxito. Recuerda realizar tu pago presencialmente el día de la consulta.
    </p>
    <div style={styles.esperaInfoCard}>
      <div style={styles.esperaInfoRow}>
        <Calendar size={18} color="#475569" />
        <span style={styles.esperaInfoText}>
          {diaSeleccionado?.diaNumero} de {diaSeleccionado?.mesCorto} · {horaSeleccionada?.label}
        </span>
      </div>
    </div>
    <button style={styles.esperaBoton} onClick={handleBack}>
      Volver al perfil
    </button>
  </div>
);

const PasoEspera = ({ profesionalNombre, diaSeleccionado, horaSeleccionada, handleBack }: any) => (
  <div style={styles.esperaContainer}>
    <div style={styles.esperaIcono}>
      <CheckCircle size={64} color="#0052CC" />
    </div>
    <h2 style={styles.esperaTitulo}>¡Comprobante enviado!</h2>
    <p style={styles.esperaSubtitulo}>
      El Dr. {profesionalNombre} revisará tu pago y confirmará tu cita. Te notificaremos cuando todo esté listo.
    </p>
    <div style={styles.esperaInfoCard}>
      <div style={styles.esperaInfoRow}>
        <Calendar size={18} color="#475569" />
        <span style={styles.esperaInfoText}>
          {diaSeleccionado?.diaNumero} de {diaSeleccionado?.mesCorto} · {horaSeleccionada?.label}
        </span>
      </div>
      <div style={styles.esperaInfoRow}>
        <Hourglass size={18} color="#475569" />
        <span style={styles.esperaInfoText}>
          Revisión en curso — normalmente menos de 24h
        </span>
      </div>
    </div>
    <button style={styles.esperaBoton} onClick={handleBack}>
      Volver al perfil
    </button>
  </div>
);

const PanelHoras = ({ diaSeleccionado, slotsDia, horaSeleccionada, setHoraSeleccionada, esMobile }: any) => {
  if (!diaSeleccionado) {
    return (
      <div style={styles.timesEmpty}>
        <Calendar size={32} color="#CBD5E1" />
        <span style={styles.timesEmptyText}>Elige<br/>un día</span>
      </div>
    );
  }
  if (slotsDia.length === 0) {
    return (
      <div style={styles.timesEmpty}>
        <Moon size={28} color="#CBD5E1" />
        <span style={styles.timesEmptyText}>Sin<br/>horarios</span>
      </div>
    );
  }
  
  return (
    <div style={{ overflowY: esMobile ? 'visible' : 'auto', maxHeight: esMobile ? 'none' : '100%' }}>
      <h3 style={{ ...styles.timesDayLabel, ...(esMobile ? { marginBottom: '12px', fontSize: '13px' } : {}) }}>
        {diaSeleccionado.diaNumero} {diaSeleccionado.mesCorto}
      </h3>
      {slotsDia.map((slot: any) => {
        const sel = horaSeleccionada?.inicio === slot.inicio;
        const disabledStyles = slot.disponible ? {} : { backgroundColor: '#F1F5F9', borderColor: '#E2E8F0', cursor: 'not-allowed', boxShadow: 'none' };
        const disabledTextStyles = slot.disponible ? {} : { color: '#94A3B8' };
        
        return (
          <button
            key={slot.inicio}
            style={{ ...styles.timeRow, ...(sel ? styles.timeRowSel : {}), ...disabledStyles, ...(esMobile ? { padding: '12px', marginBottom: '8px' } : {}) }}
            onClick={() => slot.disponible && setHoraSeleccionada(slot)}
            disabled={!slot.disponible}
          >
            <span style={{ ...styles.timeRowText, ...(sel ? styles.timeRowTextSel : {}), ...disabledTextStyles }}>
              {slot.label}
            </span>
          </button>
        );
      })}
      <div style={{ height: '24px' }} />
    </div>
  );
};

const PasoSlot = ({
  esMobile, diasDisponibles, diaSeleccionado, setDiaSeleccionado,
  horaSeleccionada, setHoraSeleccionada, slotsDia, diaActivo,
  servicios, servicioSeleccionado, setServicioSeleccionado
}: any) => {
  const semanas: (SlotDia | null)[][] = [];
  const primerDia = diasDisponibles[0];
  const primerDow = new Date(primerDia.fecha + 'T12:00:00').getDay();
  let semanaActual: (SlotDia | null)[] = new Array(primerDow).fill(null);
  for (const dia of diasDisponibles) {
    semanaActual.push(dia);
    if (semanaActual.length === 7) {
      semanas.push(semanaActual);
      semanaActual = [];
    }
  }
  if (semanaActual.length > 0) {
    while (semanaActual.length < 7) {
      semanaActual.push(null);
    }
    semanas.push(semanaActual);
  }

  const handleSelectDia = (dia: SlotDia) => {
    setDiaSeleccionado(dia);
    setHoraSeleccionada(null);
  };

  const renderDia = (dia: SlotDia | null, di: number, cellKey: string) => {
    if (!dia) {
      return <div key={cellKey} style={styles.calCell} />;
    }
    const activo = diaActivo(dia);
    const sel = diaSeleccionado?.fecha === dia.fecha;
    return (
      <button
        key={dia.fecha}
        style={{ ...styles.calCell, ...(activo ? styles.calCellActivo : {}), ...(sel ? styles.calCellSel : {}), cursor: activo ? 'pointer' : 'not-allowed' }}
        onClick={() => activo && handleSelectDia(dia)}
        disabled={!activo}
      >
        <span style={{ ...styles.calCellText, ...(activo ? {} : styles.calCellTextOff), ...(sel ? styles.calCellTextSel : {}) }}>
          {dia.diaNumero}
        </span>
      </button>
    );
  };

  const renderSemana = (sem: (SlotDia | null)[], si: number) => {
    const firstDay = sem.find((d) => d !== null);
    const weekKey = firstDay ? `week-${firstDay.fecha}` : `week-${si}`;
    return (
      <div key={weekKey} style={styles.calRow}>
        {sem.map((dia, di) => renderDia(dia, di, `${weekKey}-${di}`))}
      </div>
    );
  };

  if (esMobile) {
    return (
      <div style={{ flex: 1, backgroundColor: '#F4F7FC', padding: '12px', paddingBottom: '140px', overflowY: 'auto' }}>
        {servicios.length > 0 && (
          <div style={styles.mobileCalCard}>
            <label htmlFor="mobile-servicio-select" style={{ fontSize: '15px', fontWeight: 800, color: '#0A2540', display: 'block', marginBottom: '12px' }}>
              Servicio a agendar
            </label>
            <select
              id="mobile-servicio-select"
              value={servicioSeleccionado?.id || ''}
              onChange={(e) => setServicioSeleccionado(servicios.find((s: any) => s.id === Number(e.target.value)))}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid #E2E8F0',
                backgroundColor: '#F8FAFC', fontSize: '15px', color: '#0A2540', fontWeight: 600, outline: 'none', appearance: 'none'
              }}
            >
              {servicios.map((s: any) => (
                <option key={s.id} value={s.id}>{s.titulo_opcion} — {s.moneda === 'EUR' ? '€' : '$'}{Number(s.precio_sugerido).toFixed(2)}</option>
              ))}
            </select>
          </div>
        )}

        <div style={styles.mobileCalCard}>
          <div style={styles.mobileCalMesLabel}>
            {MESES_CORTOS[new Date(diasDisponibles[0].fecha + 'T12:00:00').getMonth()]} –{' '}
            {MESES_CORTOS[new Date(diasDisponibles[diasDisponibles.length - 1].fecha + 'T12:00:00').getMonth()]}{' '}
            {new Date(diasDisponibles[0].fecha + 'T12:00:00').getFullYear()}
          </div>
          <div style={styles.calHeaderRow}>
            {['Do','Lu','Ma','Mi','Ju','Vi','Sá'].map((d) => (
              <span key={d} style={styles.calHeaderCell}>{d}</span>
            ))}
          </div>
          {semanas.map((sem, si) => renderSemana(sem, si))}
        </div>

        <div style={styles.mobileTimesCard}>
          <PanelHoras 
            diaSeleccionado={diaSeleccionado} 
            slotsDia={slotsDia} 
            horaSeleccionada={horaSeleccionada} 
            setHoraSeleccionada={setHoraSeleccionada} 
            esMobile={esMobile}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.slotWrapper, flexDirection: 'column', gap: '24px' }}>
      {servicios.length > 0 && (
        <div style={{ width: '100%', maxWidth: '960px', backgroundColor: '#FFFFFF', padding: '20px 24px', borderRadius: '20px', boxShadow: '0 8px 24px rgba(10, 37, 64, 0.05)', border: '1px solid #E2E8F0' }}>
          <label htmlFor="desktop-servicio-select" style={{ fontSize: '15px', fontWeight: 800, color: '#0A2540', display: 'block', marginBottom: '10px' }}>
            Servicio a agendar
          </label>
          <select
            id="desktop-servicio-select"
            value={servicioSeleccionado?.id || ''}
            onChange={(e) => setServicioSeleccionado(servicios.find((s: any) => s.id === Number(e.target.value)))}
            style={{
              width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid #E2E8F0',
              backgroundColor: '#F8FAFC', fontSize: '16px', color: '#0A2540', fontWeight: 600, outline: 'none'
            }}
          >
            {servicios.map((s: any) => (
              <option key={s.id} value={s.id}>{s.titulo_opcion} — {s.moneda === 'EUR' ? '€' : '$'}{Number(s.precio_sugerido).toFixed(2)}</option>
            ))}
          </select>
        </div>
      )}

      <div style={styles.slotLayout}>
        <div style={styles.calPanel}>
          <div style={styles.calMesLabel}>
            {MESES_CORTOS[new Date(diasDisponibles[0].fecha + 'T12:00:00').getMonth()]} –{' '}
            {MESES_CORTOS[new Date(diasDisponibles[diasDisponibles.length - 1].fecha + 'T12:00:00').getMonth()]}{' '}
            {new Date(diasDisponibles[0].fecha + 'T12:00:00').getFullYear()}
          </div>
          <div style={styles.calHeaderRow}>
            {['Do','Lu','Ma','Mi','Ju','Vi','Sá'].map((d) => (
              <span key={d} style={styles.calHeaderCell}>{d}</span>
            ))}
          </div>
          {semanas.map((sem, si) => renderSemana(sem, si))}
        </div>

        <div style={styles.calDivider} />

        <div style={{ ...styles.timesPanel, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <PanelHoras 
              diaSeleccionado={diaSeleccionado} 
              slotsDia={slotsDia} 
              horaSeleccionada={horaSeleccionada} 
              setHoraSeleccionada={setHoraSeleccionada} 
              esMobile={esMobile}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function SlotFooter({ puedeReservar, handleReservar, enviandoReserva, horaSeleccionada }: any) {
  return (
    <div style={styles.footer}>
      <button
        style={{ ...styles.ctaBtn, ...(puedeReservar ? {} : styles.ctaBtnDisabled) }}
        onClick={handleReservar}
        disabled={!puedeReservar}
      >
        {enviandoReserva ? (
          <div style={styles.spinnerSmallWhite} />
        ) : (
          <>
            <Lock size={18} color={puedeReservar ? '#FFFFFF' : '#94A3B8'} style={{ marginRight: '8px' }} />
            <span style={{ ...styles.ctaBtnText, ...(puedeReservar ? {} : styles.ctaBtnTextDisabled) }}>
              {horaSeleccionada ? `Reservar ${horaSeleccionada.label}` : 'Selecciona un horario'}
            </span>
          </>
        )}
      </button>
      <div style={styles.footerCaption}>Al reservar, tienes un tiempo límite para confirmar tu pago.</div>
    </div>
  );
}

function ComprobanteFooter({ puedeEnviarComprobante, handleEnviarComprobante, enviandoReserva }: any) {
  return (
    <div style={styles.footer}>
      <button
        style={{ ...styles.ctaBtn, ...(puedeEnviarComprobante ? {} : styles.ctaBtnDisabled) }}
        onClick={handleEnviarComprobante}
        disabled={!puedeEnviarComprobante}
      >
        {enviandoReserva ? (
          <div style={styles.spinnerSmallWhite} />
        ) : (
          <>
            <Send size={18} color={puedeEnviarComprobante ? '#FFFFFF' : '#94A3B8'} style={{ marginRight: '8px' }} />
            <span style={{ ...styles.ctaBtnText, ...(puedeEnviarComprobante ? {} : styles.ctaBtnTextDisabled) }}>
              Enviar comprobante
            </span>
          </>
        )}
      </button>
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export function BookingScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profesionalUuid, profesionalNombre, profesionalTitulo } = location.state || {};
  
  // Responsive check
  const [windowWidth, setWindowWidth] = useState(globalThis.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(globalThis.innerWidth);
    globalThis.addEventListener('resize', handleResize);
    return () => globalThis.removeEventListener('resize', handleResize);
  }, []);
  const esMobile = windowWidth < 640;

  const handleBack = () => {
    if (globalThis.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/hub');
    }
  };

  // Estado del flujo
  const [paso, setPaso] = useState<Paso>('slot');

  // Selección del paciente
  const [diaSeleccionado, setDiaSeleccionado] = useState<SlotDia | null>(null);
  const [horaSeleccionada, setHoraSeleccionada] = useState<SlotHora | null>(null);
  const diasDisponibles = generarProximosDias(21);

  // Custom hook para disponibilidad
  const { cargandoDisponibilidad, slotsDia, diaActivo, politicasAgendamiento, aceptaPagoPostReserva, aceptaPagoAnticipado } = useBookingAvailability(profesionalUuid, diaSeleccionado);
  
  // Políticas
  const [politicasAceptadas, setPoliticasAceptadas] = useState(false);
  const [mostrarPoliticas, setMostrarPoliticas] = useState(false);

  // Pago
  const [modoPagoSeleccionado, setModoPagoSeleccionado] = useState<'anticipado' | 'post_reserva' | null>(null);

  // Comprobante
  const [banco, setBanco] = useState('');
  const [referencia, setReferencia] = useState('');
  const [telefono, setTelefono] = useState('');
  const [monto, setMonto] = useState('');

  // Servicios
  const [servicios, setServicios] = useState<any[]>([]);
  const [servicioSeleccionado, setServicioSeleccionado] = useState<any>(null);

  // Pago Móvil Profesional
  const [pagoMovilInfo, setPagoMovilInfo] = useState<{banco: string, telefono: string, cedula: string} | null>(null);

  // Tasa BCV
  const [tasaDolar, setTasaDolar] = useState<number>(36.5);
  const [tasaEuro, setTasaEuro] = useState<number>(39.5);

  useEffect(() => {
    // Obtener tasas de cambio
    const workerUrl = import.meta.env.VITE_CLOUDFLARE_WORKER_URL;
    const finalUrl = workerUrl 
      ? `${workerUrl}/assets/tasas_bcv.json?v=${Date.now()}` 
      : `/assets/tasas_bcv.json?v=${Date.now()}`;
    
    fetch(finalUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Error al conectar con CDN BCV');
        return res.json();
      })
      .then((data) => {
        if (data?.USD) setTasaDolar(data.USD);
        if (data?.EUR) setTasaEuro(data.EUR);
      })
      .catch((err) => {
        console.warn('Fallback activo: No se pudo obtener la tasa BCV desde el CDN', err);
      });
  }, []);

  useEffect(() => {
    if (!profesionalUuid) return;
    supabase
      .from('profesional_servicios_faq')
      .select('id, titulo_opcion, precio_sugerido, moneda')
      .eq('profesional_uuid', profesionalUuid)
      .not('precio_sugerido', 'is', null)
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          setServicios(data);
          setServicioSeleccionado(data[0]);
        }
      });

    // Cargar datos de pago móvil
    supabase
      .from('perfiles_profesionales')
      .select('pm_banco, pm_telefono, pm_cedula')
      .eq('profesional_uuid', profesionalUuid)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          if (data.pm_banco && data.pm_telefono && data.pm_cedula) {
            setPagoMovilInfo({
              banco: data.pm_banco,
              telefono: data.pm_telefono,
              cedula: data.pm_cedula
            });
          }
        }
      });
  }, [profesionalUuid]);

  // Actualizar el monto cuando cambia el servicio seleccionado o la tasa
  useEffect(() => {
    if (servicioSeleccionado) {
      const moneda = servicioSeleccionado.moneda || 'USD';
      const tasa = moneda === 'EUR' ? tasaEuro : tasaDolar;
      setMonto(new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(servicioSeleccionado.precio_sugerido * tasa));
    }
  }, [servicioSeleccionado, tasaDolar, tasaEuro]);

  // TTL countdown
  const [citaId, setCitaId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(0);

  const restorePendingCita = (data: any) => {
    const expDate = new Date(data.expires_at);
    const now = new Date();
    const diffSeconds = Math.max(0, Math.floor((expDate.getTime() - now.getTime()) / 1000));
    
    if (diffSeconds <= 0) return;

    setCitaId(data.cita_id);
    setExpiresAt(expDate);
    setSegundosRestantes(diffSeconds);
    
    const startDate = new Date(data.fecha_hora_inicio);
    setDiaSeleccionado({
       fecha: data.fecha_hora_inicio.split('T')[0],
       diaNombre: DIAS_ES[startDate.getDay()],
       diaNumero: String(startDate.getDate()).padStart(2, '0'),
       mesCorto: MESES_CORTOS[startDate.getMonth()]
    });
    
    const hIniStr = String(startDate.getHours()).padStart(2, '0');
    const mIniStr = String(startDate.getMinutes()).padStart(2, '0');
    const endDate = new Date(data.fecha_hora_fin);
    const hFinStr = String(endDate.getHours()).padStart(2, '0');
    const mFinStr = String(endDate.getMinutes()).padStart(2, '0');

    setHoraSeleccionada({
       inicio: `${hIniStr}:${mIniStr}`,
       fin: `${hFinStr}:${mFinStr}`,
       label: formatearHora(`${hIniStr}:${mIniStr}`),
       disponible: true
    });

    setServicios(prevServicios => {
      const found = prevServicios.find(s => s.id === data.servicio_id);
      if (found) setServicioSeleccionado(found);
      return prevServicios;
    });

    setPaso('comprobante');
  };

  // Recuperar cita pendiente si el usuario recarga la página
  useEffect(() => {
    if (!profesionalUuid) return;
    const fetchPendingCita = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData?.session?.user;
      if (!user) return;

      const { data, error } = await supabase
        .from('agendamiento_citas')
        .select('cita_id, expires_at, fecha_hora_inicio, fecha_hora_fin, servicio_id')
        .eq('paciente_uuid', user.id)
        .eq('profesional_uuid', profesionalUuid)
        .eq('estado', 'reservada_ttl')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) return;
      restorePendingCita(data);
    };
    fetchPendingCita();
  }, [profesionalUuid]);

  // Diálogos Personalizados (Premium UX)
  const [modalData, setModalData] = useState<{
    visible: boolean;
    titulo: string;
    mensaje: string;
    tipo: 'alert' | 'confirm';
    onConfirm?: () => void;
  }>({ visible: false, titulo: '', mensaje: '', tipo: 'alert' });

  const showAlert = (titulo: string, mensaje: string, onConfirm?: () => void) => {
    setModalData({ visible: true, titulo, mensaje, tipo: 'alert', onConfirm });
  };
  const showConfirm = (titulo: string, mensaje: string, onConfirm: () => void) => {
    setModalData({ visible: true, titulo, mensaje, tipo: 'confirm', onConfirm });
  };

  const handlePressBack = () => {
    if (paso !== 'comprobante') {
      handleBack();
      return;
    }
    showConfirm(
      '¿Cancelar reserva?',
      'Si vuelves ahora, el slot se liberará de inmediato para que otros puedan agendar.',
      async () => {
        if (citaId) {
          try {
            await supabase.from('agendamiento_citas').update({ estado: 'cancelada_paciente' }).eq('cita_id', citaId);
          } catch (e) {
            console.error('Error al liberar slot:', e);
          }
        }
        setCitaId(null);
        setExpiresAt(null);
        setSegundosRestantes(0);
        setPaso('slot');
      }
    );
  };

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      const diff = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setSegundosRestantes(diff);
      if (diff === 0) {
        clearInterval(interval);
        if (citaId) {
          supabase.from('agendamiento_citas').update({ estado: 'expirada' }).eq('cita_id', citaId).then();
        }
        showAlert(
          'Tiempo agotado',
          'El tiempo para subir tu comprobante venció. El slot fue liberado. Por favor, realiza una nueva reserva.',
          () => {
            // Reiniciar variables para permanecer en la pantalla del calendario
            setCitaId(null);
            setExpiresAt(null);
            setSegundosRestantes(0);
            setPaso('slot');
            setDiaSeleccionado(null);
            setHoraSeleccionada(null);
          }
        );
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const formatTTL = (seg: number) => `${String(Math.floor(seg / 60)).padStart(2, '0')}:${String(seg % 60).padStart(2, '0')}`;

  const { handleReservar, handleEnviarComprobante, enviandoReserva } = useBookingFlow({
    profesionalUuid, diaSeleccionado, horaSeleccionada, banco, referencia, telefono, monto,
    citaId, setCitaId, setExpiresAt, setSegundosRestantes, setPaso, navigate, handleBack,
    showAlert, showConfirm, servicioSeleccionado
  });

  if (cargandoDisponibilidad) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <span style={styles.loadingText}>Cargando disponibilidad...</span>
      </div>
    );
  }

  const puedeReservar = diaSeleccionado !== null && horaSeleccionada !== null && !enviandoReserva;
  const puedeEnviarComprobante =
    banco.trim().length > 2 &&
    referencia.length === 6 &&
    telefono.trim().length > 6 &&
    Number.parseFloat(monto) > 0 &&
    !enviandoReserva;

  let headerTitle = 'Reserva enviada';
  if (paso === 'slot') headerTitle = 'Selecciona un horario';
  else if (paso === 'metodo_pago') headerTitle = 'Método de Pago';
  else if (paso === 'comprobante') headerTitle = 'Confirmar pago';
  else if (paso === 'confirmada') headerTitle = 'Cita Confirmada';

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <button style={styles.btnBack} onClick={handlePressBack}>
          <ArrowLeft size={24} color="#0A2540" />
        </button>

        <div style={styles.headerCenter}>
          <h1 style={styles.headerTitle}>{headerTitle}</h1>
          {paso !== 'espera' && (
            <p style={styles.headerSubtitle}>{profesionalNombre}</p>
          )}
        </div>
      </div>

      {/* CONTENIDO POR PASO */}
      {paso === 'slot' && (
        <PasoSlot 
          esMobile={esMobile}
          diasDisponibles={diasDisponibles}
          diaSeleccionado={diaSeleccionado}
          setDiaSeleccionado={setDiaSeleccionado}
          horaSeleccionada={horaSeleccionada}
          setHoraSeleccionada={setHoraSeleccionada}
          slotsDia={slotsDia}
          diaActivo={diaActivo}
          servicios={servicios}
          servicioSeleccionado={servicioSeleccionado}
          setServicioSeleccionado={setServicioSeleccionado}
        />
      )}
      {paso === 'metodo_pago' && (
        <PasoMetodoPago
          aceptaPagoAnticipado={aceptaPagoAnticipado}
          aceptaPagoPostReserva={aceptaPagoPostReserva}
          politicasAgendamiento={politicasAgendamiento}
          politicasAceptadas={politicasAceptadas}
          setPoliticasAceptadas={setPoliticasAceptadas}
          setMostrarPoliticas={setMostrarPoliticas}
          modoPagoSeleccionado={modoPagoSeleccionado}
          setModoPagoSeleccionado={setModoPagoSeleccionado}
        />
      )}
      {paso === 'comprobante' && (
        <PasoComprobante 
          diaSeleccionado={diaSeleccionado}
          horaSeleccionada={horaSeleccionada}
          profesionalNombre={profesionalNombre}
          profesionalTitulo={profesionalTitulo}
          segundosRestantes={segundosRestantes}
          formatTTL={formatTTL}
          banco={banco} setBanco={setBanco}
          referencia={referencia} setReferencia={setReferencia}
          telefono={telefono} setTelefono={setTelefono}
          monto={monto} setMonto={setMonto}
          servicioSeleccionado={servicioSeleccionado}
          pagoMovilInfo={pagoMovilInfo}
          politicasAgendamiento={politicasAgendamiento}
          politicasAceptadas={politicasAceptadas}
          setPoliticasAceptadas={setPoliticasAceptadas}
          setMostrarPoliticas={setMostrarPoliticas}
        />
      )}
      {paso === 'espera' && (
        <PasoEspera 
          profesionalNombre={profesionalNombre} 
          diaSeleccionado={diaSeleccionado} 
          horaSeleccionada={horaSeleccionada} 
          handleBack={handleBack} 
        />
      )}
      {paso === 'confirmada' && (
        <PasoConfirmada 
          profesionalNombre={profesionalNombre} 
          diaSeleccionado={diaSeleccionado} 
          horaSeleccionada={horaSeleccionada} 
          handleBack={handleBack} 
        />
      )}

      {/* FOOTER CTA */}
      {paso === 'slot' && (
        <SlotFooter 
          puedeReservar={puedeReservar} 
          handleContinue={() => setPaso('metodo_pago')} 
          enviandoReserva={enviandoReserva} 
          horaSeleccionada={horaSeleccionada} 
        />
      )}
      {paso === 'metodo_pago' && (
        <MetodoPagoFooter 
          puedeContinuar={(!politicasAgendamiento || politicasAceptadas) && modoPagoSeleccionado !== null}
          handleReservar={() => handleReservar(modoPagoSeleccionado === 'post_reserva')}
          enviandoReserva={enviandoReserva}
        />
      )}
      {paso === 'comprobante' && (
        <ComprobanteFooter 
          puedeEnviarComprobante={puedeEnviarComprobante} 
          handleEnviarComprobante={handleEnviarComprobante} 
          enviandoReserva={enviandoReserva} 
        />
      )}

      {/* Modal Políticas de Agendamiento */}
      {mostrarPoliticas && politicasAgendamiento && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: '16px', width: '100%', maxWidth: '400px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Políticas de Agendamiento</h3>
              <button onClick={() => setMostrarPoliticas(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#64748B' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1, fontSize: '14px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {politicasAgendamiento}
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
              <button 
                onClick={() => { setPoliticasAceptadas(true); setMostrarPoliticas(false); }}
                style={{ width: '100%', padding: '12px', backgroundColor: '#0052CC', color: '#FFF', border: 'none', borderRadius: '100px', fontWeight: 600, cursor: 'pointer' }}
              >
                He leído y acepto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DIÁLOGO MODAL PERSONALIZADO (PREMIUM UX) */}
      {modalData.visible && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>{modalData.titulo}</h3>
            <p style={styles.modalMessage}>{modalData.mensaje}</p>
            <div style={styles.modalFooter}>
              {modalData.tipo === 'confirm' ? (
                <>
                  <button 
                    style={styles.modalBtnSecondary} 
                    onClick={() => setModalData(prev => ({ ...prev, visible: false }))}
                  >
                    Cancelar
                  </button>
                  <button 
                    style={styles.modalBtnPrimary} 
                    onClick={() => {
                      setModalData(prev => ({ ...prev, visible: false }));
                      if (modalData.onConfirm) modalData.onConfirm();
                    }}
                  >
                    Confirmar
                  </button>
                </>
              ) : (
                <button 
                  style={styles.modalBtnPrimary} 
                  onClick={() => {
                    setModalData(prev => ({ ...prev, visible: false }));
                    if (modalData.onConfirm) modalData.onConfirm();
                  }}
                >
                  Entendido
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input:focus { outline: 2px solid #0052CC; }
        button {
          appearance: none;
          -webkit-appearance: none;
          border: none;
          background: transparent;
          outline: none;
        }
        button:focus, button:active, button:focus-visible {
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
        }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { flex: 1, backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loadingContainer: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: '#FFFFFF', gap: '16px', minHeight: '100vh' },
  loadingText: { fontSize: '15px', color: '#64748B' },
  scrollContent: { padding: '20px', flex: 1, overflowY: 'auto' },
  spinner: { width: '40px', height: '40px', border: '4px solid #0052CC', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinnerSmallWhite: { width: '24px', height: '24px', border: '3px solid #FFFFFF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  header: { display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #F1F5F9', backgroundColor: '#FFF' },
  btnBack: { width: '40px', height: '40px', borderRadius: '20px', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer' },
  headerCenter: { flex: 1, margin: '0 12px' },
  headerTitle: { fontSize: '16px', fontWeight: 700, color: '#0A2540', margin: 0 },
  headerSubtitle: { fontSize: '13px', color: '#64748B', marginTop: '1px', margin: 0 },

  sectionLabel: { fontSize: '17px', fontWeight: 700, color: '#0A2540', marginBottom: '12px', marginTop: '24px' },
  sectionCaption: { fontSize: '14px', color: '#64748B', lineHeight: '20px', marginBottom: '20px', marginTop: '-8px' },

  slotWrapper: { flex: 1, backgroundColor: '#F4F7FC', padding: '16px', paddingBottom: '110px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  slotLayout: { width: '100%', maxWidth: '960px', flex: 1, maxHeight: '520px', backgroundColor: '#FFFFFF', borderRadius: '20px', display: 'flex', flexDirection: 'row', boxShadow: '0 8px 24px rgba(10, 37, 64, 0.1)', border: '1px solid #E2E8F0', overflow: 'hidden' },

  mobileCalCard: { width: '100%', backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '16px', marginBottom: '12px', boxShadow: '0 4px 12px rgba(10, 37, 64, 0.08)', border: '1px solid #E2E8F0' },
  mobileTimesCard: { width: '100%', backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '16px', boxShadow: '0 4px 12px rgba(10, 37, 64, 0.08)', border: '1px solid #E2E8F0' },
  mobileCalMesLabel: { fontSize: '15px', fontWeight: 800, color: '#0A2540', letterSpacing: '0.3px', marginBottom: '14px', paddingLeft: '2px' },

  calPanel: { flex: 1, padding: '28px 32px 32px 32px', display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' },
  calMesLabel: { fontSize: '18px', fontWeight: 800, color: '#0A2540', letterSpacing: '0.5px', marginBottom: '24px', paddingLeft: '4px' },
  calHeaderRow: { display: 'flex', flexDirection: 'row', marginBottom: '12px' },
  calHeaderCell: { flex: 1, textAlign: 'center', fontSize: '13px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' },
  calRow: { display: 'flex', flexDirection: 'row', marginBottom: '8px' },
  calCell: { flex: 1, aspectRatio: '1/1', margin: '4px', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderWidth: '0px', borderStyle: 'solid', borderColor: 'transparent', backgroundColor: 'transparent' },
  calCellActivo: { backgroundColor: '#F0F5FF', borderWidth: '1px', borderStyle: 'solid', borderColor: '#E0E9F5' },
  calCellSel: { backgroundColor: '#0052CC', borderWidth: '1px', borderStyle: 'solid', borderColor: '#0052CC', boxShadow: '0 4px 8px rgba(0, 82, 204, 0.3)' },
  calCellText: { fontSize: '16px', fontWeight: 600, color: '#1E293B' },
  calCellTextOff: { color: '#CBD5E1', fontWeight: 400 },
  calCellTextSel: { color: '#FFFFFF', fontWeight: 800 },

  calDivider: { width: '1px', backgroundColor: '#E2E8F0' },

  timesPanel: { flex: 0.7, padding: '32px', backgroundColor: '#FAFCFF' },
  timesDayLabel: { fontSize: '15px', fontWeight: 800, color: '#0052CC', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '20px', marginTop: 0 },
  timeRow: { width: '100%', padding: '16px 20px', borderRadius: '14px', borderWidth: '1px', borderStyle: 'solid', borderColor: '#E2E8F0', backgroundColor: '#FFFFFF', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(10, 37, 64, 0.03)', cursor: 'pointer', transition: 'all 0.2s' },
  timeRowSel: { backgroundColor: '#0052CC', borderWidth: '1px', borderStyle: 'solid', borderColor: '#0052CC', boxShadow: '0 4px 8px rgba(0, 82, 204, 0.2)' },
  timeRowText: { fontSize: '15px', fontWeight: 700, color: '#0052CC' },
  timeRowTextSel: { color: '#FFFFFF' },
  timesEmpty: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '10px', height: '100%' },
  timesEmptyText: { fontSize: '13px', color: '#94A3B8', textAlign: 'center', lineHeight: '18px', fontWeight: 500 },

  reservaSummaryCard: { backgroundColor: '#F0F4FA', borderRadius: '16px', padding: '16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px', border: '1px solid #DBEAFE' },
  reservaSummaryRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' },
  reservaSummaryText: { fontSize: '14px', fontWeight: 600, color: '#0A2540' },

  ttlBanner: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', backgroundColor: '#EFF6FF', borderRadius: '12px', padding: '14px', marginBottom: '24px', border: '1px solid #BFDBFE' },
  ttlBannerUrgente: { backgroundColor: '#FEF2F2', borderColor: '#FECACA' },
  ttlText: { flex: 1, fontSize: '14px', color: '#1E40AF', margin: 0 },
  ttlTextUrgente: { color: '#DC2626' },
  ttlClock: { fontWeight: 800, fontVariantNumeric: 'tabular-nums' },

  inputGroup: { marginBottom: '16px', display: 'flex', flexDirection: 'column' },
  inputLabel: { fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' },
  input: { backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '14px 16px', fontSize: '16px', color: '#0A2540', width: '100%' },
  inputHint: { fontSize: '12px', color: '#F59E0B', marginTop: '6px', marginLeft: '4px' },

  esperaContainer: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 32px' },
  esperaIcono: { width: '96px', height: '96px', borderRadius: '48px', backgroundColor: '#EFF6FF', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '24px' },
  esperaTitulo: { fontSize: '24px', fontWeight: 800, color: '#0A2540', textAlign: 'center', marginBottom: '12px', margin: '0 0 12px 0' },
  esperaSubtitulo: { fontSize: '16px', color: '#64748B', textAlign: 'center', lineHeight: '24px', marginBottom: '32px', margin: '0 0 32px 0' },
  esperaInfoCard: { width: '100%', backgroundColor: '#F8FAFC', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', border: '1px solid #E2E8F0', marginBottom: '32px' },
  esperaInfoRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' },
  esperaInfoText: { fontSize: '14px', color: '#475569', flex: 1 },
  esperaBoton: { backgroundColor: '#0052CC', padding: '16px 40px', borderRadius: '100px', border: 'none', color: '#FFFFFF', fontSize: '16px', fontWeight: 700, cursor: 'pointer' },

  footer: { position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#FFFFFF', padding: '16px 20px 36px 20px', borderTop: '1px solid #F1F5F9', boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)', zIndex: 100 },
  ctaBtn: { backgroundColor: '#0052CC', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: '17px', borderRadius: '100px', marginBottom: '8px', border: 'none', cursor: 'pointer', width: '100%', transition: 'background-color 0.2s' },
  ctaBtnDisabled: { backgroundColor: '#F1F5F9', cursor: 'not-allowed' },
  ctaBtnText: { color: '#FFFFFF', fontSize: '16px', fontWeight: 700 },
  ctaBtnTextDisabled: { color: '#94A3B8' },
  footerCaption: { fontSize: '12px', color: '#94A3B8', textAlign: 'center' },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(10, 37, 64, 0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    padding: '28px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: '0 20px 40px rgba(10, 37, 64, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 800,
    color: '#0A2540',
    marginBottom: '12px',
    marginTop: 0,
  },
  modalMessage: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '20px',
    marginBottom: '24px',
    margin: 0,
  },
  modalFooter: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    width: '100%',
  },
  modalBtnPrimary: {
    flex: 1,
    backgroundColor: '#0052CC',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '100px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'center',
  },
  modalBtnSecondary: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    color: '#475569',
    border: 'none',
    borderRadius: '100px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'center',
  },
};
